import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import DashboardTile from '../components/DashboardTile';
import api from '../utils/api';
import './StaffDashboard.css';

const StaffDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [resolvedUser, setResolvedUser] = useState(currentUser);
  const [previewTier, setPreviewTier] = useState(''); // '' = staff view
  const [allToggles, setAllToggles] = useState(null); // full tiered toggle data
  const [developerNotes, setDeveloperNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);

  const formatDate = (value) => {
    if (!value) return 'No date';
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    let mounted = true;

    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/api/users/me');
        if (mounted && response?.data) {
          console.log('[StaffDashboard] /api/users/me response:', response.data);
          setResolvedUser(response.data);
        }
      } catch (error) {
        console.error('[StaffDashboard] /api/users/me error:', error);
        if (mounted) {
          setResolvedUser(currentUser);
        }
      }
    };

    const fetchAllToggles = async () => {
      try {
        const response = await api.get('/api/feature-toggles');
        if (mounted) setAllToggles(response.data);
      } catch {
        // non-critical for preview
      }
    };

    const fetchDeveloperNotes = async () => {
      try {
        setNotesLoading(true);
        const response = await api.get('/api/developer-notes');
        if (mounted) {
          const notes = Array.isArray(response.data) ? response.data : [];
          setDeveloperNotes(notes);
        }
      } catch {
        if (mounted) setDeveloperNotes([]);
      } finally {
        if (mounted) setNotesLoading(false);
      }
    };

    fetchCurrentUser();
    fetchAllToggles();
    fetchDeveloperNotes();

    return () => {
      mounted = false;
    };
  }, [currentUser]);

  const dashboardUser = resolvedUser || currentUser;
  
  // Admins get full access regardless of individual permission flags
  const isAdmin = dashboardUser?.userType === 'admin';

  // Access permission checks based on user's assigned permissions
  // Both staff and admin users must have specific permissions assigned
  const hasEducationAccess = isAdmin || (dashboardUser && dashboardUser.education === true);
  const hasHealthAccess = isAdmin || (dashboardUser && dashboardUser.health === true);
  const hasHarvestingAccess = isAdmin || (dashboardUser && dashboardUser.harvesting === true);
  const hasOutreachAccess = isAdmin || (dashboardUser && dashboardUser.communityOutreach === true);
  const hasCouncilAccess = isAdmin || (dashboardUser && dashboardUser.council === true);
  const hasHRAccess = isAdmin || (dashboardUser && dashboardUser.hr === true);
  const hasCommunityResourcesAccess = isAdmin || (dashboardUser && dashboardUser.communityResources === true);
  const hasConsultationAccess = isAdmin || (dashboardUser && dashboardUser.consultation === true);
  const hasFaqAccess = isAdmin || (dashboardUser && dashboardUser.faqEditor === true);
  const latestNote = developerNotes[0];
  const latestVersion = latestNote?.version ? `ver ${latestNote.version}` : 'ver -';
  const latestDate = latestNote ? formatDate(latestNote.publishedAt || latestNote.createdAt) : 'No date';

  return (
    <div className="dashboard-container">
      <div className="dashboard-welcome">
        <h2>Staff Dashboard</h2>
        <p>Welcome, {dashboardUser?.firstName || dashboardUser?.name || 'Staff Member'}</p>
      </div>

      <div className="staff-release-notes">
        <button
          type="button"
          className="staff-release-notes-header"
          onClick={() => setNotesExpanded((prev) => !prev)}
        >
          <h3>{`Release Notes From Admin - ${latestVersion} - ${latestDate}`}</h3>
          <span className="staff-release-notes-chevron" aria-hidden="true">{notesExpanded ? '▾' : '▸'}</span>
        </button>
        {notesLoading ? (
          <p className="staff-release-notes-empty">Loading release notes...</p>
        ) : developerNotes.length === 0 ? (
          <p className="staff-release-notes-empty">No release notes have been published yet.</p>
        ) : notesExpanded ? (
          developerNotes.slice(0, 6).map((note) => (
            <article className="staff-release-note-item" key={note._id}>
              <div className="staff-release-note-body">
                <div className="staff-release-note-top">
                  <h4>{note.title}</h4>
                  {note.isPinned && <span className="staff-release-note-pinned">Pinned</span>}
                </div>
                <p className="staff-release-note-meta">
                  {note.version ? `ver ${note.version} - ` : ''}
                  {formatDate(note.publishedAt || note.createdAt)}
                </p>
                <p className="staff-release-note-content">{note.changes}</p>
              </div>
            </article>
          ))
        ) : null}
      </div>
      
      <div className="dashboard-tiles-container">
        {/* No permissions assigned */}
        {!isAdmin && !hasEducationAccess && !hasHealthAccess && !hasHarvestingAccess &&
         !hasOutreachAccess && !hasCouncilAccess && !hasHRAccess &&
         !hasCommunityResourcesAccess && !hasConsultationAccess && !hasFaqAccess && (
          <div style={{padding:'2rem',color:'#666',textAlign:'center'}}>
            <p>No workspace modules have been assigned to your account yet.</p>
            <p>Please contact an administrator to have your permissions configured.</p>
          </div>
        )}

        {/* Education Portal Tile */}
        {hasEducationAccess && (
          <DashboardTile 
            title="Education Portal" 
            icon="🎓"
            description="Manage education grants and review applications" 
            path="/education-dashboard"
            color="#4285F4"
          />
        )}
        
        {/* Programs Portal Tile */}
        {hasHealthAccess && (
          <DashboardTile 
            title="Programs Portal" 
            icon="🏥"
            description="Manage health programs and review applications" 
            path="/staff-health-programs"
            color="#48bb78"
          />
        )}
        
        {/* Harvesting Portal Tile */}
        {hasHarvestingAccess && (
          <DashboardTile 
            title="Harvesting Portal" 
            icon="🌾"
            description="Manage harvesting posts and updates" 
            path="/staff/harvesting"
            color="#DB4437"
          />
        )}
        
        {/* Community Outreach Tile */}
        {hasOutreachAccess && (
          <DashboardTile 
            title="Community Outreach" 
            icon="🤲"
            description="Manage community outreach programs and initiatives" 
            path="/staff/outreach"
            color="#0F9D58"
          />
        )}
        
        {/* Council Tile */}
        {hasCouncilAccess && (
          <DashboardTile 
            title="Council" 
            icon="🏛️"
            description="Council matters and governance" 
            path="/staff/council"
            color="#9C27B0"
          />
        )}
        
        {/* HR Tile */}
        {hasHRAccess && (
          <DashboardTile 
            title="Human Resources" 
            icon="👥"
            description="Human resources and staff management" 
            path="/staff/hr"
            color="#FF6F00"
          />
        )}
        
        {/* Community Resources Tile */}
        {hasCommunityResourcesAccess && (
          <DashboardTile 
            title="Community Resources" 
            icon="📚"
            description="Manage community resources and feedback" 
            path="/staff/community-resources"
            color="#AB47BC"
          />
        )}
        
        {/* Consultation Portal Tile */}
        {hasConsultationAccess && (
          <DashboardTile 
            title="Project Consultation" 
            icon="🤝"
            description="Manage community consultation projects" 
            path="/staff/consultation"
            color="#F4B400"
          />
        )}

        {/* FAQ Management Tile */}
        {hasFaqAccess && (
          <DashboardTile 
            title="FAQ Management" 
            icon="❓"
            description="Create and edit FAQ posts for the community" 
            path="/staff/faq"
            color="#6B8E23"
          />
        )}
      </div>

      {/* ── Preview As Section ────────────────────────────────────────── */}
      <div className="staff-preview-section">
        <div className="staff-preview-header">
          <h3>👁️ Preview As User Level</h3>
          <select
            className="staff-preview-select"
            value={previewTier}
            onChange={e => setPreviewTier(e.target.value)}
            aria-label="Preview as user level"
          >
            <option value="">— Staff View (current) —</option>
            <option value="public">Public (not logged in)</option>
            <option value="user">User (logged in, not citizen)</option>
            <option value="citizen">Citizen (verified member)</option>
          </select>
        </div>

        {previewTier && allToggles && (() => {
          const t = allToggles[previewTier] || {};
          const tierLabels = { public: 'Public', user: 'User', citizen: 'Citizen' };
          const tiles = [];

          // Programs / services tiles (simplified)
          if (t.education) tiles.push({ icon: '🎓', title: 'Education', description: 'Scholarships, grants, and educational support', path: '/health-programs?category=education', color: '#4285F4' });
          if (t.health)     tiles.push({ icon: '🏥', title: 'Health & Wellness', description: 'Medical services and health support', path: '/health-programs?category=health', color: '#48bb78' });
          if (t.culture)    tiles.push({ icon: '🎨', title: 'Culture', description: 'Cultural programs and language revitalization', path: '/health-programs?category=culture', color: '#ED64A6' });
          if (t.legal)      tiles.push({ icon: '⚖️', title: 'Legal Services', description: 'Legal services, treaty rights, advocacy', path: '/health-programs?category=legal', color: '#9F7AEA' });
          if (t.harvesting) tiles.push({ icon: '🌾', title: 'Harvesting', description: 'Harvesting information and blog updates', path: '/harvesting', color: '#DB4437' });
          if (t.communityResources) tiles.push({ icon: '📚', title: 'Community Resources', description: 'Access community services and contacts', path: '/community-resources', color: '#AB47BC' });
          if (t.communityOutreach)  {
            tiles.push({ icon: '📰', title: 'Community News', description: 'Latest community news and announcements', path: '/community-news', color: '#FF6347' });
            tiles.push({ icon: '📄', title: 'Community Information', description: 'Browse community pages', path: '/community-pages', color: '#4682B4' });
          }
          if (t.faq) tiles.push({ icon: '❓', title: 'FAQ', description: 'Frequently asked questions', path: '/faq', color: '#6B8E23' });
          if (t.consultation) tiles.push({ icon: '💬', title: 'Public Consultations', description: 'View active community consultation projects', path: '/consultation-map', color: '#9370DB' });
          if (t.council)  tiles.push({ icon: '🏛️', title: 'Council', description: 'Council matters and governance', path: '/council', color: '#6B8E23' });
          if (t.hr)       tiles.push({ icon: '👥', title: 'Human Resources', description: 'View available job opportunities', path: '/hr', color: '#FF8C00' });

          return (
            <div className="staff-preview-body">
              <div className="staff-preview-label">
                What <strong>{tierLabels[previewTier]}</strong> users see on their dashboard:
              </div>
              {tiles.length === 0 ? (
                <p className="staff-preview-empty">No tiles are enabled for the <strong>{tierLabels[previewTier]}</strong> tier. Check Feature Toggles in admin settings.</p>
              ) : (
                <div className="staff-preview-tiles">
                  {tiles.map((tile, i) => (
                    <DashboardTile key={i} {...tile} disabled={false} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default StaffDashboard;
